import React, { useState, useContext, useEffect } from 'react';
import { HotelContext } from '../context/hotel';
import { UserContext } from '../context/user';
import { CheckInContext } from "../context/checkin";
import { Rating } from 'react-simple-star-rating';
import { useReviews } from "../hooks/useReviews";
import RateExtraQuestions from './RateExtraQuestions';
import ReviewFlowForm from './ReviewFlowForm';
import processHtml from '../components/processHtml';
import LangSelector from "../components/LangSelector";
import { i18nValue } from '../functions';
import { getCDNImage } from "../globals";
import { T } from "react-translator-component";
import {
    getSimpleReviewConfig,
    REVIEW_SETTING_TYPES,
    SIMPLE_REVIEW_DESIGN_DEFAULTS,
    SIMPLE_REVIEW_FONT_FAMILIES,
} from "./Reviews/reviewQuestionBlocks";

const EMPTY_PREVIEW_SIMULATED_UPSELLING_IDS = [];

const DEFAULT_SIMPLE_REVIEW_HERO = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
<svg width="1200" height="1800" viewBox="0 0 1200 1800" fill="none" xmlns="http://www.w3.org/2000/svg">
  <rect width="1200" height="1800" fill="#F5EFE8"/>
  <rect x="76" y="92" width="1048" height="1616" rx="48" fill="#E7D7C8"/>
  <rect x="134" y="164" width="932" height="470" rx="34" fill="#C97C4A"/>
  <rect x="134" y="690" width="454" height="300" rx="28" fill="#B8D7D2"/>
  <rect x="612" y="690" width="454" height="300" rx="28" fill="#F4C95D"/>
  <rect x="134" y="1048" width="932" height="552" rx="34" fill="#FFF9F4"/>
  <circle cx="928" cy="310" r="118" fill="#F6E8DA"/>
  <circle cx="280" cy="1262" r="104" fill="#D8B08C"/>
  <path d="M250 1415C333.395 1340.06 440.841 1298.48 558 1298.48C675.159 1298.48 782.605 1340.06 866 1415" stroke="#C97C4A" stroke-width="30" stroke-linecap="round"/>
  <path d="M454 390L585 271L746 432" stroke="#FFF9F4" stroke-width="34" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`)}`;

const getSimpleReviewImage = (value = "") => {
    if (!value) {
        return DEFAULT_SIMPLE_REVIEW_HERO;
    }
    if (/^https?:\/\//i.test(value) || value.startsWith("data:")) {
        return value;
    }
    return getCDNImage(value);
};

const findRateIdByCode = (sections, code) => {
    const section = sections.find(section => section.code === code);
    if (section) {
        return section?.extras?.rateId;
    } else {
        return null;
    }
};

const Rate = ({ type, service, settingId, serviceId, onRate, reviewsBundle, standaloneReviewFlow }) => {
    const { language } = useContext(UserContext);
    const { sections, data: hotelData } = useContext(HotelContext);
    const { data: { id: checkInId, roomOccupationId } } = useContext(CheckInContext);
    const [sending, setSending] = useState(false);
    const [ratingId, setRatingId] = useState(false);
    const [showExtraQuestionsModal, setShowExtraQuestionsModal] = useState(false);
    const [ratingClicked, setRatingClicked] = useState(false);
    const [flowInitFailed, setFlowInitFailed] = useState(false);

    let reviewSettingId = null;
    if (type === "general") {
        reviewSettingId = settingId;
    }
    if (type === "checkIn") {
        reviewSettingId = findRateIdByCode(sections, 'fast_checkin');

    }
    if (type === "request") {
        reviewSettingId = findRateIdByCode(sections, `service_${service}`);
    }

    const skipInternalSettingFetch = type === "general" && reviewsBundle != null;
    const internalReviews = useReviews(skipInternalSettingFetch ? null : reviewSettingId);
    const data = skipInternalSettingFetch ? reviewsBundle.data : internalReviews.data;
    const reviewFlow = skipInternalSettingFetch ? reviewsBundle.reviewFlow : internalReviews.reviewFlow;
    const reviewSettingType = skipInternalSettingFetch ? reviewsBundle.reviewSettingType : internalReviews.reviewSettingType;
    const create = skipInternalSettingFetch ? reviewsBundle.create : internalReviews.create;
    const previewMode = !!reviewsBundle?.isPreview;
    const previewSelectedNodeId = reviewsBundle?.previewSelectedNodeId || null;
    const previewPlayMode = !!reviewsBundle?.previewPlayMode;
    const previewSimulatedUpsellingIds = previewMode
      ? reviewsBundle?.previewSimulatedUpsellingIds?.length
        ? reviewsBundle.previewSimulatedUpsellingIds
        : EMPTY_PREVIEW_SIMULATED_UPSELLING_IDS
      : EMPTY_PREVIEW_SIMULATED_UPSELLING_IDS;
    const simpleReviewConfig = getSimpleReviewConfig(data?.questions);
    const simpleHeroImage = getSimpleReviewImage(simpleReviewConfig?.heroImage || "");
    const simpleReviewDesign = simpleReviewConfig?.design || SIMPLE_REVIEW_DESIGN_DEFAULTS;
    const simpleReviewTitle = i18nValue(data || {}, "name", language, null, data?.name || T("review.rate_us"));
    const simpleReviewMessage = i18nValue(data || {}, "text", language, null, data?.text || "");
    const hotelLogo = hotelData?.logo ? getCDNImage(hotelData.logo) : "";
    const textAlign = simpleReviewDesign?.textAlign === "left" ? "left" : "center";
    const cardItemsAlign = textAlign === "left" ? "flex-start" : "center";
    const ratingJustify = textAlign === "left" ? "flex-start" : "center";
    const simpleFontFamily = SIMPLE_REVIEW_FONT_FAMILIES[simpleReviewDesign?.fontFamily] || SIMPLE_REVIEW_FONT_FAMILIES.modern;

    const flowSessionKey = `flowReviewRatingId_${reviewSettingId || settingId}`;
    const isFlowMode = reviewSettingType === REVIEW_SETTING_TYPES.FLOW;
    const showStarStep = !isFlowMode || flowInitFailed;

    useEffect(() => {
        if (!data || !reviewSettingId || !isFlowMode || flowInitFailed || previewMode) {
            return;
        }
        try {
            const existing = sessionStorage.getItem(flowSessionKey);
            if (existing) {
                setRatingId(existing);
                setShowExtraQuestionsModal(true);
                return;
            }
        } catch (e) {
            /* ignore */
        }

        setSending(true);
        // review es obligatorio en backend (Int!); 0 = placeholder hasta valor real en el flujo
        create(reviewSettingId, 0, checkInId, serviceId)
            .then((reviewId) => {
                if (reviewId) {
                    try {
                        sessionStorage.setItem(flowSessionKey, reviewId);
                    } catch (e) {
                        /* ignore */
                    }
                    setRatingId(reviewId);
                    setShowExtraQuestionsModal(true);
                } else {
                    setFlowInitFailed(true);
                }
            })
            .catch(() => {
                setFlowInitFailed(true);
            })
            .finally(() => {
                setSending(false);
            });
    }, [data, reviewSettingId, isFlowMode, flowInitFailed, create, checkInId, serviceId, flowSessionKey, previewMode]);

    const handleRating = value => {
        if (previewMode) {
            if (standaloneReviewFlow && !isFlowMode) {
                setRatingId("preview-review");
                setShowExtraQuestionsModal(true);
            }
            return;
        }
        if (ratingClicked) return;
        setRatingClicked(true);
        setSending(true);

        create(reviewSettingId, value, checkInId, serviceId).then(reviewId => {
            if (reviewId) {
                console.log('20ago - id', reviewId)
                setRatingId(reviewId);
                setSending(false);
                localStorage.setItem(`anonymousVoteData${settingId}`, JSON.stringify({ review: value }));
                setShowExtraQuestionsModal(true)
            }
        });

        // if (type === "general") {
        //     rate(reviewSettingId, value).then(r => {
        //         if (r) {
        //             console.log('20go - id', r)
        //             setRatingId(r);
        //             setSending(false);
        //             localStorage.setItem(`anonymousVoteData${settingId}`, JSON.stringify({ review: value }));
        //             setShowExtraQuestionsModal(true)
        //         }
        //     });
        // }
        // if (type === "checkIn") {
        //     rateCheckIn(reviewSettingId, checkInId, value).then(r => {
        //         if (r) {
        //             console.log('20go - id', r)
        //             setRatingId(r)
        //             setSending(false);
        //             setShowExtraQuestionsModal(true)
        //         }
        //     });
        // } else if (type === "request") {
        //     rateRequest(reviewSettingId, serviceId, value).then(r => {
        //         if (r) {
        //             console.log('20go - id', r)
        //             setRatingId(r)
        //             setSending(false);
        //             setShowExtraQuestionsModal(true)
        //         }
        //     });
        // }
    }


    if (!data || !reviewSettingId) return false;

    if (standaloneReviewFlow && previewMode && isFlowMode) {
        return (
            <ReviewFlowForm
                ratingId="preview-review"
                reviewFlow={reviewFlow}
                reviewSettingType={reviewSettingType}
                language={language}
                settingId={settingId}
                roomOccupationId={roomOccupationId}
                layout="fullscreen"
                previewMode
                previewSelectedNodeId={previewSelectedNodeId}
                previewPlayMode={previewPlayMode}
                previewSimulatedUpsellingIds={previewSimulatedUpsellingIds}
                onFinish={(previewResult) => {
                    if (onRate) onRate(previewResult);
                }}
            />
        );
    }

    const useFullscreenFlow =
        standaloneReviewFlow && isFlowMode && !flowInitFailed && ratingId;

    const useFullscreenSimple =
        standaloneReviewFlow && !isFlowMode && showExtraQuestionsModal && (ratingId || previewMode);

    if (useFullscreenFlow) {
        return (
            <ReviewFlowForm
                ratingId={ratingId}
                reviewFlow={reviewFlow}
                reviewSettingType={reviewSettingType}
                language={language}
                settingId={settingId}
                roomOccupationId={roomOccupationId}
                layout="fullscreen"
                onFinish={() => {
                    setShowExtraQuestionsModal(false);
                    if (onRate) onRate();
                }}
            />
        );
    }

    if (useFullscreenSimple) {
        return (
            <ReviewFlowForm
                ratingId={ratingId || "preview-review"}
                reviewFlow={reviewFlow}
                reviewSettingType={reviewSettingType}
                simpleReviewConfig={simpleReviewConfig}
                language={language}
                settingId={settingId}
                roomOccupationId={roomOccupationId}
                layout="fullscreen"
                previewMode={previewMode}
                previewSelectedNodeId={previewSelectedNodeId}
                previewPlayMode={previewPlayMode}
                previewSimulatedUpsellingIds={previewSimulatedUpsellingIds}
                onFinish={(previewResult) => {
                    setShowExtraQuestionsModal(false);
                    if (onRate) onRate(previewResult);
                }}
            />
        );
    }

    if (standaloneReviewFlow && isFlowMode && !flowInitFailed && (sending || !ratingId)) {
        return (
            <div
                className="d-flex justify-content-center align-items-center py-5"
                style={{ minHeight: 240 }}
            >
                <div className="spinner-border" role="status" />
            </div>
        );
    }

    return (
        <>
            {standaloneReviewFlow && !isFlowMode ? (
                <div
                    className="d-flex flex-column w-100"
                    style={{
                        minHeight: "100%",
                        background: `center / cover no-repeat url("${simpleHeroImage}")`,
                        color: simpleReviewDesign.textColor,
                        position: "relative",
                        justifyContent: "center",
                        padding: "88px 16px 32px",
                    }}
                >
 
                        <div
                            style={{
                                position: "absolute",
                                top: 14,
                                left: "50%",
                                transform: "translateX(-50%)",
                                zIndex: 5,
                            }}
                        >
                            <LangSelector />
                        </div>
                 
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            background: "linear-gradient(180deg, rgba(15, 23, 42, 0.10) 0%, rgba(15, 23, 42, 0.22) 100%)",
                            pointerEvents: "none",
                        }}
                    />
                    <div
                        className="d-flex flex-column align-items-center"
                        style={{
                            width: "100%",
                            position: "relative",
                            zIndex: 1,
                            gap: 16,
                        }}
                    >
                        <div
                            className="d-flex flex-column"
                            style={{
                                width: "100%",
                                maxWidth: simpleReviewDesign.cardWidth,
                                margin: "0 auto",
                                padding: "32px 18px 36px",
                                color: simpleReviewDesign.textColor,
                                background: simpleReviewDesign.cardBackgroundColor,
                                borderRadius: simpleReviewDesign.cardBorderRadius,
                                boxShadow: "0 18px 40px rgba(15, 23, 42, 0.22)",
                                fontFamily: simpleFontFamily,
                            }}
                        >
                            <div
                                style={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: cardItemsAlign,
                                    textAlign,
                                }}
                            >
            {simpleReviewDesign.showHotelLogo && hotelLogo ? (
                            <img
                                src={hotelLogo}
                                alt=""
                                style={{
                                    width: "auto",
                                    maxWidth: 180,
                                    height: simpleReviewDesign.logoSize,
                                    objectFit: "contain",
                                    display: "block",
                                    marginBottom: 25,
                                }}
                            />
                        ) : null}

                                <h1 style={{
                                    fontSize: simpleReviewDesign.titleFontSize,
                                    lineHeight: 1.2,
                                    fontWeight: 500,
                                    marginBottom: 18,
                                    color: simpleReviewDesign.textColor,
                                    width: "100%",
                                }}>
                                    {simpleReviewTitle}
                                </h1>
                                {simpleReviewMessage ? (
                                    <div style={{
                                        fontSize: simpleReviewDesign.messageFontSize,
                                        lineHeight: 1.55,
                                        color: simpleReviewDesign.textColor,
                                        marginBottom: 34,
                                        width: "100%",
                                    }}>
                                        {processHtml(simpleReviewMessage)}
                                    </div>
                                ) : null}
                                <div className="d-flex" style={{ paddingTop: 4, justifyContent: ratingJustify, width: "100%" }}>
                                    {showStarStep && !sending ? (
                                        <Rating
                                            onClick={handleRating}
                                            SVGstyle={{ display: "inline-block" }}
                                            initialValue={0}
                                            fillColor={simpleReviewDesign.accentColor}
                                            emptyColor={simpleReviewDesign.accentMutedColor}
                                            iconsCount={5}
                                            size={simpleReviewDesign.ratingSize}
                                            allowFraction={false}
                                        />
                                    ) : null}
                                    {sending ? (
                                        <div className="loading">
                                            <div className="spinner-border" role="status" />
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {showStarStep ? (
                        <div style={{ marginTop: 20, fontSize: 20, opacity: 1 }}>{T("review.rate_us")}</div>
                    ) : null}
                    <div className="border-top p-4" style={{ marginTop: 20 }}>
                        <div>
                            {data?.text ? <p>{processHtml(i18nValue(data, 'text', language))} </p> : null}
                            {showStarStep && !sending && (
                                <Rating
                                    onClick={handleRating}
                                    initialValue={0}
                                    fillColor="#D4A72C"
                                    emptyColor="#D1D5DB"
                                    iconsCount={5}
                                    allowFraction={false}
                                />
                            )}
                            {sending && (
                                <div className="loading">
                                    <div className="spinner-border" role="status" />
                                </div>
                            )}
                        </div>
                    </div>
                </>
            )}

                {showExtraQuestionsModal && ratingId && !standaloneReviewFlow ? (
                    <RateExtraQuestions
                        roomOccupationId={roomOccupationId}
                        ratingId={ratingId}
                        reviewFlow={reviewFlow}
                        reviewSettingType={reviewSettingType}
                        setShowExtraQuestionsModal={setShowExtraQuestionsModal}
                        showExtraQuestionsModal={showExtraQuestionsModal}
                        language={language}
                        onRate={onRate}
                        settingId={settingId}
                    />
                ) : null}
        </>
    );

}

export default Rate;
